<template>
  <div align="center" class="wrapper background-white">
    <success v-if="isSuccessModalVisible"></success>
    <div class="block-center mt-xl wd-xl">
      <!-- START panel-->
      <div class="panel panel-dark panel-flat">
        <div class="panel-body" v-if="needsMfa">
          <p class="text-center pv">Multi-factor Authentication</p>
          <p class="text-center pv">Please enter the code sent to your email.</p>
          <strong><p class="text-red">{{ otherErrors }}</p></strong>
          <form role="form" class="mb-lg" @submit.prevent="validateMfaAndSubmitLogin()" novalidate>
            <input v-model="mfaCode" id="mfaCode" type="text"
              placeholder="Enter the code" autocomplete="off" required class="form-control">
            <button type="submit" class="btn btn-block btn-default btn-lg mt-lg" :disabled="submittedMfa">Submit</button>
          </form>
          <p class="pt-lg text-center border-top mt-xl">
            Back to <a v-on:click="backToLogin" class="cursor-pointer">login</a>
          </p>
        </div>
        <div class="panel-body" v-if="!needsMfa">
          <p class="text-center">Log in to your account below | <a class="text-muted cursor-pointer" @click="handleRedirection()">First time user?</a></p>
          <strong><p class="text-red">{{ otherErrors }}</p></strong>
          <p v-if="isUserIdle"><strong class="text-red">You have been logged out of your account due to inactivity.</strong></p>
          <p v-if="fromPasswordResetEmail"><strong class="text-success">Your password has been reset successfully.</strong></p>
          <form role="form" class="mb-lg" @submit.prevent="validateAndSubmitLogin()" novalidate>
            <div class="form-group has-feedback" v-bind:class="{ 'has-error': emailError }">
              <input v-model="email" id="signupInputEmail1" type="email"
              placeholder="Enter Email" autocomplete="off" required class="form-control shadow">
              <span id="helpBlock2" class="help-block">{{ emailError }}</span>
            </div>

            <div class="form-group has-feedback" v-bind:class="{ 'has-error': passwordError }">
              <input v-model="password" id="signupInputPassword1" type="password"
              placeholder="Enter Password" autocomplete="off" required class="form-control shadow">
              <span id="helpBlock4" class="help-block">{{ passwordError }}</span>
            </div>

            <div class="direction-row">
              <div><a class="text-muted cursor-pointer" @click="handleRedirection('reset-password')" >Forgot your password?</a>
              </div>
              <div class="direction-row">
                <a
                @click="handleRememberMe()"
                class="text-muted no-underline mr-1 cursor-pointer"
                >
                  Remember Me
                </a>
                  <input type="checkbox" value="" class="checkbox-shadow cursor-pointer" name="rememberMe" v-model="rememberMe">
              </div>
            </div>

            <button
            type="submit"
            class="btn btn-pill btn-default btn-lg mt-lg pull-left mb-0"
            :disabled="submittedForm">
            <strong>
              <em class="icon-lock"></em>
              Log In
            </strong> </button>

            <div class="form-group has-feedback" v-bind:class="{ 'has-error': loginError }">
              <strong id="helpBlock6" class="help-block">{{ loginError }}</strong>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapMutations, mapState } from 'vuex';

  import { isEmpty } from 'lodash';

  import {
    SET_ACCOUNT_STATUS,
    SET_ACTIVE_ACCOUNT_ID,
    SET_IDLE_OFF,
    SET_NEW_USER_EMAIL,
    SET_REDIRECT_URL,
    SET_USER,
  } from '@/store/mutation-types';

  import success from '@/components/modals/Success';
  import { logAction, logglyTags, postMessageSuccessEmbedLogin } from '@/util';
  import logActions from '@/data/static/logActions';
  import emailErrorsTypes from '@/data/static/emailErrorsTypes';

  export default {
    name: 'embed-login',
    created() {
      const { email, success: successMsg, message } = this.$route.query;
      if (successMsg === 'false') {
        this.$router.push({ name: 'ErrorAuth', query: { email, message, success: successMsg, type: emailErrorsTypes.CONFIRMATION_EMAIL.name } });
      }
    },
    beforeMount() {
      document.title = 'Calvert Impact Capital Platform Login';
      const email = localStorage.getItem('email');
      this.setRedirectUrl(this.$route.query.redirectUrl);
      if (email) {
        this.email = email;
        this.rememberMe = true;
      }
    },
    data() {
      return {
        email: '',
        emailError: '',
        password: '',
        passwordError: '',
        mfaCode: '',
        showPassword: false,
        loginError: '',
        otherErrors: '',
        rememberMe: false,
        needsMfa: false,
        submittedForm: false,
        submittedMfa: false,
      };
    },
    computed: {
      ...mapState('authentication', [
        'isUserIdle',
        'redirectUrl',
      ]),
      fromPasswordResetEmail() {
        return !!this.$route.query.resetEmail;
      },
      ...mapState('success', [
        'isSuccessModalVisible',
      ]),
      ...mapState('app', [
        'accountTypes',
        'activeAccount',
        'irAdmin',
        'userTypes',
      ]),
    },
    components: {
      success,
    },
    methods: {
      ...mapMutations('app', {
        setUser: SET_USER,
        setAccountStatus: SET_ACCOUNT_STATUS,
        setActiveAccountId: SET_ACTIVE_ACCOUNT_ID,
      }),
      ...mapMutations('user', {
        setNewUserEmail: SET_NEW_USER_EMAIL,
      }),
      ...mapMutations('authentication', {
        setRedirectUrl: SET_REDIRECT_URL,
        setIdleOff: SET_IDLE_OFF,
      }),
      ...mapActions('app', [
        'getStaticInformation',
      ]),
      ...mapActions('authentication', [
        'logIn',
        'handleMfa',
      ]),
      ...mapActions('accountCreation', [
        'getUserProfile',
      ]),
      handleRememberMe() {
        this.rememberMe = !this.rememberMe;
      },
      handleRedirection(urlEndpoint = '') {
        const URL = `${window.origin}/#/${urlEndpoint}`;
        postMessageSuccessEmbedLogin(URL);
      },
      backToLogin() {
        this.needsMfa = false;
        this.submittedForm = false;
        this.submittedMfa = false;
        this.otherErrors = '';
        this.mfaCode = '';
      },
      resetValidations() {
        this.emailError = '';
        this.passwordError = '';
        this.otherErrors = '';
      },
      validateAndSubmitLogin() {
        this.submittedForm = true;
        this.resetValidations();
        this.setIdleOff();
        if (!this.validateEmail(this.email)
            || !this.validatePassword(this.password)) {
          return false;
        }
        const { email, password } = this;
        return this.logIn({ email, password })
          .then((response) => {
            if (response && response.needsMfa) {
              this.needsMfa = response.needsMfa;
              return;
            }
            this.continueLogin(email);
          })
          .catch((error) => {
            this.submittedForm = false;
            this.otherErrors = error.description;
          });
      },
      validateMfaAndSubmitLogin() {
        this.submittedMfa = true;
        const { mfaCode } = this;
        if (mfaCode) {
          this.handleMfa({ mfaCode })
            .then(() => {
              this.continueLogin(this.email);
            })
            .catch((error) => {
              this.submittedMfa = false;
              this.otherErrors = error.error_description
                .replace('binding_code', 'code')
                .replace('mfa_token', 'Code');
            });
        }
      },
      continueLogin(email) {
        const emailToSave = this.rememberMe ? this.email : '';
        const originPath = `${window.location.origin}/#`;
        localStorage.setItem('email', emailToSave);
        this.getUserProfile().then((user) => {
          logAction({
            user: user.userId,
            level: 'info',
            action: logActions.LOGIN.name,
            message: `User logged in ${user.userId} ${email}`,
            tags: [logglyTags.auth0],
          });
          this.setUser(user);
          this.checkForAccountStatus(user);
          if (user.roles.includes('IR_ADMIN')) {
            postMessageSuccessEmbedLogin(`${originPath}/users`);
          } else {
            if (this.redirectUrl && this.redirectUrl.includes('funding-sources')) {
              postMessageSuccessEmbedLogin(`${originPath}/funding-sources`);
              return;
            }
            postMessageSuccessEmbedLogin(`${originPath}/account-summary`);
          }
          window.dataLayer.push({
            event: 'login',
            userID: user.userId || `new_user:${this.email}`,
          });
          this.setNewUserEmail(this.email);
          this.submittedForm = false;
          this.submittedMfa = false;
        });
      },
      checkForAccountStatus(userInformation) {
        let account = {};
        let doesUserHaveAccounts = false;

        if (userInformation.accounts && userInformation.accounts.length) {
          doesUserHaveAccounts = true;
        }

        if (doesUserHaveAccounts) {
          account = userInformation.accounts.find(
            a => a.id === userInformation.defaultAccountId,
          );
        }
        if (isEmpty(account)) {
          account = {
            accountName: `${userInformation.firstName} ${userInformation.lastName}`,
            id: 0,
            accountType: userInformation.userType,
            status: undefined,
          };
          if (account.accountType !== 'ADMIN') {
            account.accountType = 'INDIVIDUAL';
          }
        }

        this.setActiveAccountId(account);
        this.setAccountStatus(account.status);
      },
      validateEmail(email) {
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line no-useless-escape
        if (!regex.test(email)) {
          this.emailError = 'Please, enter a valid email';
          return false;
        }
        return true;
      },
      validatePassword(password) {
        if (password.length < 1) {
          this.passwordError = 'Please enter a password';
          return false;
        }
        return true;
      },
    },
  };
</script>

<style>
  body {
    background-color: #fff;
  }
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .form-control {
    border-radius: 0;
    background: transparent;
    font-style: italic;
  }
  .shadow {
    box-shadow: inset 0 0px 3.5px 0 gray!important;
  }
  .checkbox-shadow {
    box-shadow: inset 0 0 3.5px 0 gray!important;
  }
  .wd-xl {
    width: 360px;
  }
  .text-red {
    color: #a94442;
  }
  .panel-body {
    padding: 0 0;
  }
  .form-icon {
    position: absolute;
    top: 10px;
    right: 12px;
  }
  .cursor-pointer {
    cursor: pointer;
  }
  .btn-pill {
    border-radius: 50px;
  }
  .direction-row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-content: center;
  }
  .mr-1 {
    margin-right: 3px;
  }
  .no-underline {
    text-decoration: none;
  }
  .mb-0 {
    margin-bottom: 0;
  }
</style>



// WEBPACK FOOTER //
// EmbedLogin.vue?5e487371